import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Filter,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Calendar,
  ArrowRight,
  Play,
  User,
  Zap,
} from 'lucide-react';

/**
 * SchedulerHistory - View scheduler run history
 */
const SchedulerHistory = ({ currentUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const hasPermission = isSuperAdmin || currentUser?.admin_permissions?.includes('billing_scheduler');

  const fetchHistory = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setPage(0);
        setRuns([]);
      }

      const offset = reset ? 0 : page * 50;
      const params = new URLSearchParams({ limit: '50', offset: offset.toString() });
      if (filter) {
        params.append('task_type', filter);
      }

      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/history?${params}`,
        { headers: getAuthHeaders() }
      );

      const newRuns = response.data;
      setHasMore(newRuns.length === 50);

      if (reset) {
        setRuns(newRuns);
      } else {
        setRuns((prev) => [...prev, ...newRuns]);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to fetch scheduler history');
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    if (hasPermission) {
      setLoading(true);
      fetchHistory(true);
    } else {
      setLoading(false);
    }
  }, [hasPermission, filter]);

  const fetchRunDetails = async (runId) => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/history/${runId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedRun(response.data);
    } catch (err) {
      console.error('Error fetching run details:', err);
      setError('Failed to fetch run details');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };

  const formatAmount = (cents) => {
    if (!cents) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getTaskIcon = (taskType) => {
    switch (taskType) {
      case 'billing':
        return <DollarSign className="w-4 h-4" />;
      case 'promo_endings':
        return <Calendar className="w-4 h-4" />;
      case 'cancellations':
        return <XCircle className="w-4 h-4" />;
      case 'scheduled_changes':
        return <ArrowRight className="w-4 h-4" />;
      case 'all':
        return <Zap className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getTaskLabel = (taskType) => {
    switch (taskType) {
      case 'billing':
        return 'Billing';
      case 'promo_endings':
        return 'Promo Endings';
      case 'cancellations':
        return 'Cancellations';
      case 'scheduled_changes':
        return 'Scheduled Changes';
      case 'all':
        return 'All Tasks';
      default:
        return taskType;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
            <AlertTriangle className="w-3 h-3" />
            Partial
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
            <XCircle className="w-3 h-3" />
            Failed
          </span>
        );
      case 'running':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
            <Loader2 className="w-3 h-3 animate-spin" />
            Running
          </span>
        );
      default:
        return null;
    }
  };

  const getTriggerBadge = (trigger) => {
    switch (trigger) {
      case 'scheduled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
            <Clock className="w-3 h-3" />
            Scheduled
          </span>
        );
      case 'manual':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
            <User className="w-3 h-3" />
            Manual
          </span>
        );
      case 'api':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
            <Zap className="w-3 h-3" />
            API
          </span>
        );
      default:
        return null;
    }
  };

  if (!hasPermission) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">
              You don't have permission to access the scheduler history.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/scheduler')}
            className="p-2 hover:bg-gray-100 rounded-lg bg-white"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Scheduler History</h1>
            <p className="text-gray-600">View past scheduler runs and results</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
          >
            <option value="">All Task Types</option>
            <option value="billing">Billing</option>
            <option value="promo_endings">Promo Endings</option>
            <option value="cancellations">Cancellations</option>
            <option value="scheduled_changes">Scheduled Changes</option>
          </select>
          <button
            onClick={() => fetchHistory(true)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <XCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            &times;
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2">Loading history...</span>
        </div>
      )}

      {/* History Table */}
      {!loading && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date/Time</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Task</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Trigger</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Processed</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Duration</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {runs.map((run) => (
                <React.Fragment key={run.id}>
                  <tr
                    className={`hover:bg-gray-50 cursor-pointer ${
                      selectedRun?.id === run.id ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => {
                      if (selectedRun?.id === run.id) {
                        setSelectedRun(null);
                      } else {
                        fetchRunDetails(run.id);
                      }
                    }}
                  >
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium">{formatDate(run.started_at)}</div>
                      {run.dry_run && (
                        <span className="text-xs text-blue-600">(Dry Run)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        {getTaskIcon(run.task_type)}
                        {getTaskLabel(run.task_type)}
                      </div>
                    </td>
                    <td className="px-4 py-3">{getTriggerBadge(run.trigger)}</td>
                    <td className="px-4 py-3">{getStatusBadge(run.status)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-green-600">{run.succeeded}</span>
                      <span className="text-gray-400"> / </span>
                      <span>{run.processed}</span>
                      {run.failed > 0 && (
                        <span className="text-red-600 ml-1">({run.failed} failed)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDuration(run.duration_seconds)}
                    </td>
                    <td className="px-4 py-3">
                      {selectedRun?.id === run.id ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                  </tr>

                  {/* Expanded Details */}
                  {selectedRun?.id === run.id && (
                    <tr>
                      <td colSpan="7" className="px-4 py-4 bg-gray-50">
                        <RunDetails run={selectedRun} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Empty State */}
          {runs.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No scheduler runs found</p>
            </div>
          )}

          {/* Load More */}
          {hasMore && runs.length > 0 && (
            <div className="p-4 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  setPage((p) => p + 1);
                  fetchHistory(false);
                }}
                className="px-4 py-2 text-blue-600 hover:text-blue-800"
              >
                Load more...
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * RunDetails - Expanded details for a scheduler run
 */
const RunDetails = ({ run }) => {
  const formatAmount = (cents) => {
    if (!cents) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="space-y-4">
      {/* Run Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Run ID:</span>
          <div className="font-mono text-xs">{run.id}</div>
        </div>
        {run.triggered_by_name && (
          <div>
            <span className="text-gray-500">Triggered By:</span>
            <div>{run.triggered_by_name}</div>
          </div>
        )}
        <div>
          <span className="text-gray-500">Started:</span>
          <div>{new Date(run.started_at).toLocaleString()}</div>
        </div>
        {run.completed_at && (
          <div>
            <span className="text-gray-500">Completed:</span>
            <div>{new Date(run.completed_at).toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Results */}
      {run.results && run.results.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2">
            Results ({run.results.length})
          </h4>
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200 max-h-60 overflow-y-auto">
            {run.results.map((result, index) => (
              <div
                key={index}
                className={`px-3 py-2 flex items-center justify-between ${
                  result.success ? '' : 'bg-red-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {result.success ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <div>
                    <span className="font-medium">{result.organization_name}</span>
                    {result.package_name && (
                      <span className="ml-2 text-sm text-gray-500">{result.package_name}</span>
                    )}
                    {result.error && (
                      <div className="text-sm text-red-600">{result.error}</div>
                    )}
                  </div>
                </div>
                {result.amount && (
                  <span className="font-medium">{formatAmount(result.amount)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Errors */}
      {run.errors && run.errors.length > 0 && (
        <div>
          <h4 className="font-medium text-red-700 mb-2">
            Errors ({run.errors.length})
          </h4>
          <div className="bg-red-50 rounded-lg border border-red-200 divide-y divide-red-200">
            {run.errors.map((error, index) => (
              <div key={index} className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="font-medium">{error.organization_name || error.subscription_id}</span>
                </div>
                <div className="text-sm text-red-700 mt-1">{error.error}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty results */}
      {(!run.results || run.results.length === 0) && (!run.errors || run.errors.length === 0) && (
        <div className="text-center py-4 text-gray-500">
          No items were processed in this run
        </div>
      )}
    </div>
  );
};

export default SchedulerHistory;
